import React, { useState, useEffect } from 'react'
import {
  getCardBrandIcon,
  STRIPE_PRIMARY,
  STRIPE_TEXT_PRIMARY,
  STRIPE_TEXT_SECONDARY,
} from 'constants/stripe'

interface PaymentMethod {
  id: string
  card?: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  }
  type: string
  default: boolean
}

interface PaymentMethodDisplayProps {
  paymentMethods?: PaymentMethod[]
  isLoading?: boolean
  error?: string | null
  selectedPaymentMethodId?: string | null
  onPaymentMethodSelect?: (paymentMethodId: string | null) => void
  onAddNewCard?: () => void
  onPaymentDueDaysChange?: (days: number) => void
  invoicePreview?: {
    data: {
      preview: {
        collection_method: 'charge_automatically' | 'send_invoice'
      }
    }
  }
}

const PaymentMethodDisplay: React.FC<PaymentMethodDisplayProps> = ({
  paymentMethods = [],
  isLoading = false,
  error = null,
  selectedPaymentMethodId,
  onPaymentMethodSelect,
  onAddNewCard,
  onPaymentDueDaysChange,
  invoicePreview,
}) => {
  const [paymentDueDays, setPaymentDueDays] = useState(15)
  const [selectedOption, setSelectedOption] = useState<
    'auto_charge' | 'email_invoice' | null
  >(null)

  useEffect(() => {
    if (selectedOption !== null) return
    const method = invoicePreview?.data?.preview?.collection_method
    if (method === 'charge_automatically') {
      setSelectedOption('auto_charge')
      if (paymentMethods.length > 0 && !selectedPaymentMethodId) {
        const defaultMethod =
          paymentMethods.find((pm) => pm.default) || paymentMethods[0]
        if (defaultMethod) {
          onPaymentMethodSelect?.(defaultMethod.id)
        }
      }
    } else if (method === 'send_invoice') {
      setSelectedOption('email_invoice')
      onPaymentMethodSelect?.(null)
    }
  }, [
    invoicePreview,
    paymentMethods,
    selectedPaymentMethodId,
    onPaymentMethodSelect,
    selectedOption,
  ])

  if (isLoading) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: '#f6f9fc',
          borderRadius: '4px',
          border: '1px solid #e6ebf1',
        }}
      >
        <div style={{ color: STRIPE_TEXT_SECONDARY }}>
          Loading payment method...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: '#fef2f2',
          borderRadius: '4px',
          border: '1px solid #fecaca',
        }}
      >
        <div style={{ color: '#dc2626' }}>
          Error loading payment method: {error}
        </div>
      </div>
    )
  }

  const formatExpiry = (month: number, year: number) => {
    const formattedMonth = month.toString().padStart(2, '0')
    const formattedYear = year.toString().slice(-2)
    return `${formattedMonth}/${formattedYear}`
  }

  const handleOptionSelect = (option: 'auto_charge' | 'email_invoice') => {
    setSelectedOption(option)
    if (option === 'auto_charge') {
      if (!selectedPaymentMethodId) {
        const defaultMethod =
          paymentMethods.find((pm) => pm.default) || paymentMethods[0]
        if (defaultMethod) {
          onPaymentMethodSelect?.(defaultMethod.id)
        }
      }
    } else {
      onPaymentMethodSelect?.(null)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: 'none',
      }}
    >
      {/* Option 1: Automatically charge a payment method on file */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '16px',
          cursor: 'pointer',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '8px',
        }}
        onClick={() => handleOptionSelect('auto_charge')}
      >
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: `2px solid ${
              selectedOption === 'auto_charge' ? STRIPE_PRIMARY : '#cbd5e1'
            }`,
            backgroundColor: 'white',
            position: 'relative',
            marginTop: '2px',
          }}
        >
          {selectedOption === 'auto_charge' && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: STRIPE_PRIMARY,
              }}
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              color: '#000000',
              fontWeight: '500',
              marginBottom: '8px',
            }}
          >
            Automatically charge a payment method on file
          </div>
        </div>
      </div>

      {/* Payment methods section - only show when auto charge option is selected */}
      {selectedOption === 'auto_charge' && (
        <>
          {/* Payment method options */}
          {(paymentMethods || []).map((paymentMethod, index) => (
            <div key={paymentMethod.id}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '16px',
                  cursor: 'pointer',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  marginLeft: '32px',
                }}
                onClick={() => {
                  // Toggle selection - if already selected, deselect it
                  const newSelection =
                    selectedPaymentMethodId === paymentMethod.id
                      ? null
                      : paymentMethod.id
                  onPaymentMethodSelect?.(newSelection)
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${
                      selectedPaymentMethodId === paymentMethod.id
                        ? STRIPE_PRIMARY
                        : '#cbd5e1'
                    }`,
                    backgroundColor: 'white',
                    position: 'relative',
                    marginTop: '2px',
                  }}
                >
                  {selectedPaymentMethodId === paymentMethod.id && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: STRIPE_PRIMARY,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {getCardBrandIcon(paymentMethod.card?.brand || 'card')}
                    </div>
                    <div style={{ color: '#000000', fontSize: '14px' }}>
                      {paymentMethod.card?.brand
                        ? paymentMethod.card.brand.charAt(0).toUpperCase() +
                          paymentMethod.card.brand.slice(1).toLowerCase()
                        : 'Card'}{' '}
                      •••• {paymentMethod.card?.last4 || '****'}
                    </div>
                    {paymentMethod.default && (
                      <span
                        style={{
                          backgroundColor: STRIPE_PRIMARY,
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginLeft: '8px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        Default
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      color: '#000000',
                      fontSize: '12px',
                      marginTop: '4px',
                    }}
                  >
                    Expires{' '}
                    {formatExpiry(
                      paymentMethod.card?.exp_month || 0,
                      paymentMethod.card?.exp_year || 0
                    )}
                  </div>
                </div>
              </div>
              {index < paymentMethods.length - 1 && (
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#e2e8f0',
                    margin: '0 48px',
                  }}
                />
              )}
            </div>
          ))}

          {/* Add Card Link - moved under payment methods when auto charge is selected */}
          <div
            style={{ marginTop: '12px', textAlign: 'left', marginLeft: '32px' }}
          >
            <button
              type="button"
              onClick={onAddNewCard}
              style={{
                background: 'none',
                border: 'none',
                color: STRIPE_PRIMARY,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                padding: '0',
                textDecoration: 'none',
              }}
            >
              Add Card
            </button>
          </div>
        </>
      )}

      {/* Divider */}
      <div
        style={{
          height: '1px',
          backgroundColor: '#e2e8f0',
          margin: '0 16px',
        }}
      />

      {/* Option 2: Email invoice to the customer to pay manually */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '16px',
          cursor: 'pointer',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '8px',
        }}
        onClick={() => handleOptionSelect('email_invoice')}
      >
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: `2px solid ${
              selectedOption === 'email_invoice' ? STRIPE_PRIMARY : '#cbd5e1'
            }`,
            backgroundColor: 'white',
            position: 'relative',
            marginTop: '2px',
          }}
        >
          {selectedOption === 'email_invoice' && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: STRIPE_PRIMARY,
              }}
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              color: '#000000',
              fontWeight: '500',
              marginBottom: '8px',
            }}
          >
            Email invoice to the customer to pay manually
          </div>
          <div
            style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '12px' }}
          >
            Set your payment preferences, and we'll take care of the rest.
          </div>

          {/* Payment due settings - only show when email option is selected */}
          {selectedOption === 'email_invoice' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#000000', fontSize: '14px' }}>
                Payment due
              </span>
              <input
                type="number"
                value={paymentDueDays}
                onChange={(e) => {
                  const days = Number(e.target.value)
                  setPaymentDueDays(days)
                  onPaymentDueDaysChange?.(days)
                }}
                style={{
                  width: '60px',
                  height: '32px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  textAlign: 'center',
                  backgroundColor: 'white',
                  color: STRIPE_TEXT_PRIMARY,
                  fontSize: '14px',
                  outline: 'none',
                }}
                min="1"
                max="365"
              />
              <span style={{ color: '#000000', fontSize: '14px' }}>days</span>
              <span style={{ color: '#000000', fontSize: '14px' }}>
                after invoice is sent
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaymentMethodDisplay
